import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Download, Clock, CheckCircle, XCircle, FileText } from 'lucide-react';
import { API_BASE_URL } from '../config/api';

export default function UserDashboard() {
  const [purchases, setPurchases] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role === 'admin') {
      navigate('/');
      return;
    }
    fetchPurchases();
    fetchApplications();
  }, [user, navigate]);

  const fetchPurchases = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/payments/my-purchases-detailed`,
        { withCredentials: true }
      );
      setPurchases(data);
    } catch (error) {
      console.error('Error fetching purchases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/funding-options/applications/my-applications`,
        { withCredentials: true }
      );
      setApplications(data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    } finally {
      setApplicationsLoading(false);
    }
  };

  const handleDownload = async (paperId, paperTitle) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/papers/${paperId}/download`,
        { 
          withCredentials: true,
          responseType: 'blob',
          headers: {
            'Accept': 'application/pdf'
          }
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${paperTitle}.pdf`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      if (error.response?.status === 403) {
        alert(error.response?.data?.detail || 'Access expired. Please purchase again.');
      } else {
        alert('Download failed. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E87722] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your purchases...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Add hero section for header visibility */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#1E3A5F]/90 pt-32 pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold text-white mb-2">My Dashboard</h1>
          <p className="text-gray-200">View and download your purchased research papers</p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Purchases</p>
                <p className="text-3xl font-bold text-[#1E3A5F]">{purchases.length}</p>
              </div>
              <FileText className="h-10 w-10 text-[#E87722] opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Active Access</p>
                <p className="text-3xl font-bold text-green-600">
                  {purchases.filter(p => p.access_status?.has_access).length}
                </p>
              </div>
              <CheckCircle className="h-10 w-10 text-green-600 opacity-50" />
            </div>
          </Card>

          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Expired</p>
                <p className="text-3xl font-bold text-red-600">
                  {purchases.filter(p => p.access_status?.expired).length}
                </p>
              </div>
              <XCircle className="h-10 w-10 text-red-600 opacity-50" />
            </div>
          </Card>
        </div>

        {/* Membership Quick Links */}
        <Card className="mb-8 p-6" data-testid="my-membership-card">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-semibold mb-1">My Membership</h2>
              <p className="text-sm text-gray-600">Access the member directory or complete your institutional verification</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate('/members')} data-testid="view-directory-btn">
                View Member Directory
              </Button>
              <Button variant="outline" onClick={() => navigate('/my-engagements')} data-testid="my-engagements-btn">
                My Engagements
              </Button>
              <Button variant="outline" onClick={() => navigate('/membership/profile')} data-testid="edit-profile-btn">
                Edit My Profile
              </Button>
              <Button className="bg-[#1E3A5F] hover:bg-[#2d5a8f]" onClick={() => navigate('/membership/documents')} data-testid="upload-documents-btn">
                Verification Documents
              </Button>
            </div>
          </div>
        </Card>

        {/* Funding Applications Section */}
        <Card className="mb-8 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">My Funding Applications</h2>
            <Button
              onClick={() => navigate('/funding-options')}
              className="bg-[#E87722] hover:bg-[#d16b1a]"
            >
              Browse Funds
            </Button>
          </div>

          {applicationsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#E87722] mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading applications...</p>
            </div>
          ) : applications.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-gray-600 mb-4">You haven't submitted any funding applications yet.</p>
              <Button
                onClick={() => navigate('/funding-options')}
                className="bg-[#E87722] hover:bg-[#d16b1a]"
              >
                Explore Funding Options
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {applications.map((app) => (
                <div key={app.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{app.fund_name}</h3>
                      <p className="text-sm text-gray-600">{app.organization_name}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      app.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                      app.status === 'under_review' ? 'bg-yellow-100 text-yellow-800' :
                      app.status === 'sent_to_vc' ? 'bg-purple-100 text-purple-800' :
                      app.status === 'approved' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {app.status.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="grid md:grid-cols-3 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-gray-600">Amount:</span>
                      <span className="ml-2 font-medium">₹{app.funding_amount_requested} Lakhs</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Sector:</span>
                      <span className="ml-2">{app.sector || 'N/A'}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Submitted:</span>
                      <span className="ml-2">{new Date(app.submitted_at).toLocaleDateString()}</span>
                    </div>
                  </div>

                  {app.csed_review_notes && (
                    <div className="bg-blue-50 p-3 rounded-md mt-3">
                      <p className="text-sm font-medium text-blue-900 mb-1">CSED Review Notes:</p>
                      <p className="text-sm text-blue-800">{app.csed_review_notes}</p>
                    </div>
                  )}

                  {app.vc_feedback && (
                    <div className="bg-green-50 p-3 rounded-md mt-3">
                      <p className="text-sm font-medium text-green-900 mb-1">Fund Feedback:</p>
                      <p className="text-sm text-green-800">{app.vc_feedback}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>


        {/* Purchases List */}
        {purchases.length === 0 ? (
          <Card className="p-12 text-center bg-white">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Purchases Yet</h3>
            <p className="text-gray-600 mb-6">Browse our platform to access research papers</p>
            <Button 
              onClick={() => navigate('/research-library')}
              className="bg-[#E87722] hover:bg-[#d66a1a] text-white"
            >
              Browse Platform
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-4">Your Purchases</h2>
            {purchases.map((purchase) => (
              <Card key={purchase.purchase_id} className="p-6 bg-white hover:shadow-lg transition-shadow">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  {/* Paper Info */}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-[#1E3A5F] mb-2">
                      {purchase.paper_title}
                    </h3>
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center">
                        <FileText className="h-4 w-4 mr-1" />
                        {purchase.paper_pillar}
                      </span>
                      <span>•</span>
                      <span>{purchase.paper_theme}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <span className="text-gray-500">
                        Purchased: {new Date(purchase.purchased_at).toLocaleDateString()}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span className="font-semibold text-[#E87722]">
                        ₹{purchase.amount}
                      </span>
                    </div>
                  </div>

                  {/* Access Status & Actions */}
                  <div className="flex flex-col items-end gap-3">
                    {/* Status Badge */}
                    {purchase.access_status?.has_access ? (
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        {purchase.access_status.days_remaining} days left
                      </div>
                    ) : purchase.access_status?.expired ? (
                      <div className="flex items-center gap-2 px-3 py-1 bg-red-50 text-red-700 rounded-full text-sm font-medium">
                        <XCircle className="h-4 w-4" />
                        Expired
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                        Processing
                      </div>
                    )}

                    {/* Download Button */}
                    {purchase.access_status?.has_access ? (
                      <Button
                        onClick={() => handleDownload(purchase.paper_id, purchase.paper_title)}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    ) : purchase.access_status?.expired ? (
                      <Button
                        onClick={() => navigate(`/marketplace/${purchase.paper_id}`)}
                        className="bg-[#E87722] hover:bg-[#d66a1a] text-white"
                      >
                        Purchase Again
                      </Button>
                    ) : null}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
